import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { showErrorModal } from "../../../util/general";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 36px; height: 36px;
  animation: ${spin} 1s linear infinite;
`;

const OverlayText = styled.div`
  margin-top: 1em;
  color: white;
  font-size: 1.2em;
  text-align: center;
`;

const Container = styled.div`
  padding: 20px;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 16px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 12px;
    width: 400px;
    margin: 0 auto;
  }
`;

const ExportWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;

  @media (max-width: ${breakpoints.phone}px) {
    justify-content: center;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 16px 0;
  align-items: center;

  label {
    margin-right: 8px;
    font-size: 14px;
    color: #000;
    font-weight: 600;
  }

  select {
    padding: 5px 10px;
    width: 250px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    width: 400px;
    label {
      margin-right: 0;
    }

    select {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;

  @media (max-width: ${breakpoints.tablet}px) {
    overflow-x: auto;
  }

  @media (max-width: ${breakpoints.phone}px) {
    max-width: 400px;
    margin: 0 auto;
  }
`;

const Table = styled.table`
  ${adminTable};
  table-layout: fixed;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    position: relative;
    word-wrap: break-word;
    white-space: normal;
    padding: 0.75em 8px;

    .sort-icons {
      position: absolute;
      right: 0px;
      margin-left: 5px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 10px;
        user-select: none;
        color: #9CA3AF;
        display: block;
      }
      .active {
        color: #A855F7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
    word-wrap: break-word;
    white-space: normal;
    padding: 10px 8px;
  }

  thead th:nth-child(1),
  tbody td:nth-child(1) {
    width: 12%;
  }

  thead th:nth-child(2),
  tbody td:nth-child(2) {
    width: 17%;
  }

  thead th:nth-child(3),
  tbody td:nth-child(3) {
    width: 8%;
    text-align: center;
  }

  thead th:nth-child(4),
  tbody td:nth-child(4) {
    width: 14%;
  }

  thead th:nth-child(5),
  tbody td:nth-child(5) {
    width: 16%;
  }

  thead th:nth-child(6),
  tbody td:nth-child(6) {
    width: 7%;
    text-align: center;
  }

  thead th:nth-child(7),
  tbody td:nth-child(7) {
    width: 12%;
    text-align: right;
  }

  thead th:nth-child(8),
  tbody td:nth-child(8) {
    width: 10%;
  }

  thead th:nth-child(9),
  tbody td:nth-child(9) {
    width: 12%;
    text-align: center;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    min-width: 680px;

    thead th,
    tbody td {
      font-size: 11px;
      padding: 8px 6px;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    min-width: 600px;
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;

    button {
      margin-left: 0;
      flex: 1 0 auto;
    }
  }
`;

const LinkButton = styled.button`
  display: block;
  padding: 5px 10px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
    text-align: center;
  }
`;

export default function QuotesContent({ colors }) {
  const [quotes, setQuotes]         = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(12);
  const [sortKey, setSortKey]         = useState("inserted");
  const [sortOrder, setSortOrder]     = useState("DESC");

  const [loadingType, setLoadingType] = useState(null);
  // loadingType: 'pdf', 'csv', or 'all'

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const resp = await API.getRenewalQuote({
          page: currentPage - 1,
          items_per_page: pageSize,
          expired: 0,
          order_by: sortKey,
          order: sortOrder,
        });
        setQuotes(resp.quotes || []);
        setTotalItems(resp.total ?? resp.quotes.length);
      } catch (err) {
        console.error("Failed to load renewal quotes:", err);
      }
    };
    fetchQuotes();
  }, [currentPage, pageSize, sortKey, sortOrder]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a   = document.createElement("a");
    a.href    = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (type, payload) => {
  setLoadingType(type);
  try {
    let blob, filename;

    if (type === "pdf") {
      ({ blob, filename } = await API.exportQuotePDF(payload));
    } else if (type === "csv") {
      ({ blob, filename } = await API.exportQuoteCSV(payload));
    } else {
      ({ blob, filename } = await API.exportAllQuotesCSV({ source: "all" }));
    }

    downloadBlob(blob, filename);
  } catch (err) {
    console.error("Download failed:", err);
    showErrorModal("Download failed: " + err.message);
  } finally {
    setLoadingType(null);
  }
};


  const columns = [
    { key: "org_type",      label: "Quote Type"   },
    { key: "org_name",      label: "Organization" },
    { key: "num_of_school", label: "# Schools"    },
    { key: "contact_name",  label: "Contact"      },
    { key: "item",          label: "Item"         },
    { key: "quantity",      label: "Qty"          },
    { key: "amount",        label: "Amount($)"    },
    { key: "inserted",      label: "Submitted"    },
    { key: null,            label: ""             },
  ];

  const pageCount = pageSize > 0
    ? Math.ceil(totalItems / pageSize)
    : 1;
  const startItem = pageSize > 0
    ? (currentPage - 1) * pageSize + 1
    : 1;
  const endItem = pageSize > 0
    ? Math.min(currentPage * pageSize, totalItems)
    : totalItems;

  return (
    <Container>
      {loadingType && (
        <LoadingOverlay>
          <Spinner colors={colors} />
          <OverlayText>
            {loadingType === "pdf" && "Exporting PDF… please wait"}
            {loadingType === "csv" && "Exporting CSV… please wait"}
            {loadingType === "all" && "Exporting all… please wait"}
          </OverlayText>
        </LoadingOverlay>
      )}

      <ExportWrapper>
        <LinkButton
          colors={colors}
          onClick={() => handleDownload("all")}
        >
          Export All (CSV)
        </LinkButton>
      </ExportWrapper>

      <ControlsContainer colors={colors}>
        <label>Show</label>
        <select
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          {[12, 25, 50, 100, -1].map(n => (
            <option key={n} value={n}>
              {n === -1 ? "All" : n}
            </option>
          ))}
        </select>
        <label style={{marginLeft: 5}}>entries</label>
      </ControlsContainer>

      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              {columns.map(({ key, label }) => {
                const isAscActive = key && sortKey === key && sortOrder === "ASC";
                const isDescActive = key && sortKey === key && sortOrder === "DESC";
                const defaultColor = "#9CA3AF";
                const activeColor = "#A855F7";
                return (
                  <th key={label}>
                    {label}
                    {key && (
                      <div className="sort-icons">
                        <span
                          onClick={() => { setSortKey(key); setSortOrder("ASC"); }}
                          className={isAscActive ? "active" : ""}
                          style={{
                            color: isAscActive ? activeColor : defaultColor,
                            display: isDescActive ? "none" : "block"
                          }}
                        >
                          ▲
                        </span>
                        <span
                          onClick={() => { setSortKey(key); setSortOrder("DESC"); }}
                          className={isDescActive ? "active" : ""}
                          style={{
                            color: isDescActive ? activeColor : defaultColor,
                            display: isAscActive ? "none" : "block"
                          }}
                        >
                          ▼
                        </span>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {quotes.map(q => (
              <tr key={q.quote_id}>
                <td>{q.org_type}</td>
                <td>{q.org_name}</td>
                <td>{q.num_of_school}</td>
                <td>{q.contact_name}</td>
                <td>{q.item}</td>
                <td>{q.quantity}</td>
                <td>${(Number(q.quantity)* Number(q.price)).toLocaleString()}</td>
                <td>{q.inserted.split(" ")[0]}</td>
                <td>
                  <LinkButton
                    colors={colors}
                    onClick={() => handleDownload("pdf", { quote_id: q.quote_id })}
                    style={{ marginBottom: "5px" }}
                  >
                    PDF
                  </LinkButton>
                  <LinkButton
                    colors={colors}
                    onClick={() => handleDownload("csv", { quote_id: q.quote_id })}
                  >
                    CSV
                  </LinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <InfoText colors={colors}>
        Showing {startItem} to {endItem} of {totalItems} entries
      </InfoText>

      <Pagination colors={colors}>
        <button
          onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.min(p + 1, pageCount))}
          disabled={currentPage === pageCount}
        >
          Next
        </button>
      </Pagination>
    </Container>
  );
}
